"use client";

import { useEffect, useMemo, useState } from "react";
import { Crown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { UpgradeDialog } from "@/components/UpgradeDialog";

export const FREE_ANALYSIS_COMPLETED_EVENT = "analysis:completed";

interface AnalysisQuotaCardProps {
  className?: string;
  used: number;
  limit: number | null;
  planLabel?: string;
}

export function AnalysisQuotaCard({
  className,
  used,
  limit,
}: AnalysisQuotaCardProps) {
  const initialLimit = useMemo(
    () => (typeof limit === "number" && limit > 0 ? limit : null),
    [limit]
  );
  const initialUsed = useMemo(() => {
    if (typeof used !== "number") return 0;
    return Math.max(used, 0);
  }, [used]);
  const [currentUsed, setCurrentUsed] = useState(initialUsed);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  useEffect(() => {
    setCurrentUsed(initialUsed);
  }, [initialUsed]);

  useEffect(() => {
    if (initialLimit === null) return;

    const handle = () => {
      setCurrentUsed((prev) => {
        if (prev >= initialLimit) {
          return initialLimit;
        }
        return Math.min(prev + 1, initialLimit);
      });
    };

    window.addEventListener(FREE_ANALYSIS_COMPLETED_EVENT, handle);
    return () => {
      window.removeEventListener(FREE_ANALYSIS_COMPLETED_EVENT, handle);
    };
  }, [initialLimit]);

  if (initialLimit === null) {
    return null;
  }

  const safeLimit = initialLimit;
  const usedCount = Math.min(currentUsed, safeLimit);
  const remainingCount = Math.max(safeLimit - usedCount, 0);
  const isDepleted = remainingCount <= 0;
  const usagePercent = safeLimit
    ? Math.min((usedCount / safeLimit) * 100, 100)
    : usedCount > 0
    ? 100
    : 0;

  return (
    <div className={cn("mx-auto max-w-2xl", className)}>
      <div
        className={cn(
          "flex flex-col gap-3 rounded-2xl border-2 p-4 shadow-xl",
          isDepleted ? "border-destructive/60" : "border-amber-400/60"
        )}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div
              className={cn(
                "text-sm font-semibold",
                isDepleted
                  ? "text-destructive"
                  : "text-amber-600 dark:text-amber-400"
              )}
            >
              {isDepleted ? "Analysis Limit Reached" : "Analysis Limit"}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="text-xs text-muted-foreground">
                {remainingCount}{" "}
                {remainingCount === 1 ? "analysis left" : "analyses left"}
              </div>
              <span className="text-muted-foreground">·</span>
              <div className="text-xs text-muted-foreground">
                {usedCount}/{safeLimit} used
              </div>
            </div>
          </div>

          <div className="hidden sm:flex sm:flex-col sm:items-end sm:gap-1">
            <Button
              type="button"
              size="sm"
              variant="default"
              className="h-8 px-4 text-xs gap-1.5"
              onClick={() => setShowUpgradeDialog(true)}
            >
              <Crown className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
              Unlock All Features
            </Button>
          </div>
        </div>

        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-700",
              isDepleted ? "bg-destructive" : "bg-foreground"
            )}
            style={{ width: `${usagePercent}%` }}
            aria-hidden
          />
        </div>

        <div className="flex flex-col gap-1 sm:hidden">
          <Button
            type="button"
            size="sm"
            variant="default"
            className="w-full h-8 px-4 text-xs gap-1.5"
            onClick={() => setShowUpgradeDialog(true)}
          >
            <Crown className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
            Unlock All Features
          </Button>
        </div>

        <UpgradeDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          showMaybeLater={false}
        />
      </div>
    </div>
  );
}
